import setupWasm from 'argon2id/lib/setup.js';
import simdWasmUrl from 'argon2id/dist/simd.wasm';
import nonSimdWasmUrl from 'argon2id/dist/no-simd.wasm';

// argon2id's own default export (`import x from 'argon2id'`) assumes a
// bundler with a dedicated .wasm loader (webpack's wasm-loader / Rollup's
// plugin-wasm) that turns a `.wasm` import into an already-instantiated
// module. Create React App's default (non-ejected) webpack config instead
// resolves `.wasm` imports to a plain asset URL string — calling that as a
// function throws "is not a function". Using the library's documented
// "custom Wasm loaders" path instead: fetch + WebAssembly.instantiateStreaming
// ourselves, verified against a real dev-server + browser run before writing
// the rest of this module on top of it.
let argon2idPromise: ReturnType<typeof setupWasm> | null = null;
const loadArgon2id = () => {
  if (!argon2idPromise) {
    argon2idPromise = setupWasm(
      (importObject) => WebAssembly.instantiateStreaming(fetch(simdWasmUrl), importObject),
      (importObject) => WebAssembly.instantiateStreaming(fetch(nonSimdWasmUrl), importObject)
    );
  }
  return argon2idPromise;
};

interface KdfParams {
  memory: number; // KiB
  iterations: number;
  parallelism: number;
}

// ~64 MiB / 3 passes / 1 lane: a common client-side Argon2id starting point
// (server-side recommendations run heavier since they don't need to feel
// responsive in a browser tab) — takes roughly 1-2s on modern hardware.
const DEFAULT_KDF_PARAMS: KdfParams = { memory: 65536, iterations: 3, parallelism: 1 };

const NONCE_LENGTH = 12; // bytes — standard AES-GCM nonce size
const CHUNK_NONCE_PREFIX_LENGTH = 4; // bytes — the random part of the per-chunk nonce
const GCM_TAG_LENGTH = 16; // bytes, appended by WebCrypto to every AES-GCM ciphertext

const toBase64 = (bytes: Uint8Array): string => btoa(String.fromCharCode(...Array.from(bytes)));

const fromBase64 = (b64: string): Uint8Array =>
  Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

const generateSalt = (): Uint8Array => crypto.getRandomValues(new Uint8Array(16));

const generateNonce = (): Uint8Array => crypto.getRandomValues(new Uint8Array(NONCE_LENGTH));

// Derives a Key Encryption Key from the user's backup passphrase. Never
// sent to celepus-api — only the resulting wrapped master key is.
const deriveKek = async (
  passphrase: string,
  salt: Uint8Array,
  kdfParams: KdfParams = DEFAULT_KDF_PARAMS
): Promise<CryptoKey> => {
  const argon2id = await loadArgon2id();
  const rawKey = argon2id({
    password: new TextEncoder().encode(passphrase),
    salt,
    parallelism: kdfParams.parallelism,
    passes: kdfParams.iterations,
    memorySize: kdfParams.memory,
    tagLength: 32,
  });

  return crypto.subtle.importKey('raw', rawKey, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
};

const generateAesKey = (): Promise<CryptoKey> =>
  crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);

// Master Key: one per user, generated once client-side, never sent to the
// server in plaintext.
const generateMasterKey = generateAesKey;

// Data Encryption Key: one per file, wrapped with the master key.
const generateDek = generateAesKey;

interface WrappedKey {
  wrapped: string; // base64
  nonce: string; // base64
}

const wrapKey = async (key: CryptoKey, kek: CryptoKey): Promise<WrappedKey> => {
  const nonce = generateNonce();
  const raw = await crypto.subtle.exportKey('raw', key);
  const wrapped = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce }, kek, raw);
  return { wrapped: toBase64(new Uint8Array(wrapped)), nonce: toBase64(nonce) };
};

const unwrapKey = async (wrapped: WrappedKey, kek: CryptoKey): Promise<CryptoKey> => {
  const raw = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: fromBase64(wrapped.nonce) },
    kek,
    fromBase64(wrapped.wrapped)
  );
  return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, true, ['encrypt', 'decrypt']);
};

// Builds the 12-byte per-chunk nonce: a 4-byte random per-file prefix plus
// an 8-byte big-endian chunk counter. The counter alone already guarantees
// no nonce reuse within a file (each file has its own DEK); the random
// prefix is defense-in-depth against a DEK ever being accidentally reused.
const chunkNonce = (prefix: Uint8Array, chunkIndex: number): Uint8Array => {
  const nonce = new Uint8Array(NONCE_LENGTH);
  nonce.set(prefix, 0);
  const view = new DataView(nonce.buffer);
  // chunkIndex fits comfortably in the low 32 bits for any realistic file
  // size (2^32 chunks at 8MiB/chunk is exabytes), so only the last 4 bytes
  // of the 8-byte counter field are ever non-zero.
  view.setUint32(CHUNK_NONCE_PREFIX_LENGTH + 4, chunkIndex, false);
  return nonce;
};

// Encrypts a File in fixed-size chunks, one AES-256-GCM operation per
// chunk (ciphertext with its 16-byte auth tag appended — no manual
// concatenation, that's exactly what crypto.subtle.encrypt returns). This
// is what makes the scheme work with multipart upload: one encrypted
// chunk = one upload part, streamed rather than buffering the whole file.
async function* encryptFileToChunks(
  file: File,
  dek: CryptoKey,
  chunkSize: number,
  chunkNoncePrefix: Uint8Array
): AsyncGenerator<Uint8Array> {
  const reader = file.stream().getReader();
  let buffer = new Uint8Array(0);
  let chunkIndex = 0;
  let done = false;

  const encryptAndYield = async function* (plaintext: Uint8Array) {
    const nonce = chunkNonce(chunkNoncePrefix, chunkIndex);
    const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce }, dek, plaintext);
    chunkIndex += 1;
    yield new Uint8Array(ciphertext);
  };

  while (!done) {
    const result = await reader.read();
    done = result.done;
    if (result.value) {
      const combined = new Uint8Array(buffer.length + result.value.length);
      combined.set(buffer);
      combined.set(result.value, buffer.length);
      buffer = combined;
    }

    while (buffer.length >= chunkSize) {
      yield* encryptAndYield(buffer.slice(0, chunkSize));
      buffer = buffer.slice(chunkSize);
    }
  }

  // Final (possibly empty, for a zero-byte file) partial chunk.
  yield* encryptAndYield(buffer);
}

// Reverses encryptFileToChunks given the full ciphertext (as downloaded
// from celepus-api's presigned GET). Chunk boundaries are computed from
// the known plaintext chunkSize plus the fixed 16-byte GCM tag overhead —
// no in-body framing needed, since the true total length is authoritative
// from what was actually downloaded.
const decryptObjectBytes = async (
  ciphertext: ArrayBuffer,
  dek: CryptoKey,
  chunkNoncePrefixB64: string,
  chunkSize: number
): Promise<Uint8Array> => {
  const chunkNoncePrefix = fromBase64(chunkNoncePrefixB64);
  const encryptedChunkSize = chunkSize + GCM_TAG_LENGTH;
  const bytes = new Uint8Array(ciphertext);

  const plaintextChunks: Uint8Array[] = [];
  let chunkIndex = 0;
  for (let offset = 0; offset < bytes.length; offset += encryptedChunkSize) {
    const chunk = bytes.slice(offset, offset + encryptedChunkSize);
    const nonce = chunkNonce(chunkNoncePrefix, chunkIndex);
    const plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: nonce }, dek, chunk);
    plaintextChunks.push(new Uint8Array(plaintext));
    chunkIndex += 1;
  }

  const total = plaintextChunks.reduce((sum, c) => sum + c.length, 0);
  const result = new Uint8Array(total);
  let pos = 0;
  for (const chunk of plaintextChunks) {
    result.set(chunk, pos);
    pos += chunk.length;
  }
  return result;
};

export type {
  KdfParams,
  WrappedKey,
};

export {
  DEFAULT_KDF_PARAMS,
  CHUNK_NONCE_PREFIX_LENGTH,
  toBase64,
  fromBase64,
  generateSalt,
  deriveKek,
  generateMasterKey,
  generateDek,
  wrapKey,
  unwrapKey,
  encryptFileToChunks,
  decryptObjectBytes,
};
